// ─────────────────────────────────────────────────────────────────────────────
// Stock-split adjustment for user transactions.
//
// FMP market data (current price, historical closes, technicals) is ALWAYS
// split-adjusted. User transactions are stored raw (as originally entered). So
// after a split, valuing raw share counts at adjusted prices understates the
// position (e.g. a 10:1 split makes qty×price look 10× too small). This module
// reconciles the two by adjusting BUY/SELL quantity & price to the post-split
// basis at computation time — WITHOUT mutating the stored raw transactions.
//
// Split factor = numerator / denominator:
//   • 10:1 forward split → factor 10  → quantity ×10, price ÷10
//   • 1:5  reverse split → factor 0.2 → quantity ×0.2, price ÷0.2 (×5)
//
// ── Double-adjustment safeguard ──────────────────────────────────────────────
// We only adjust a transaction when the split's ex-date is AFTER the moment the
// record was ENTERED (createdAt). Rationale:
//   • Recorded BEFORE the split → the figures are pre-split → must be adjusted.
//   • Recorded AFTER the split   → the user already typed post-split figures
//     (their broker shows the new count) → adjusting again would double-count.
// This makes the feature safe for users who already manually fixed a split.
// ─────────────────────────────────────────────────────────────────────────────

// Coerce a timestamp of unknown shape (ms number, seconds number, or ISO/date
// string) into epoch milliseconds. Returns NaN when unparseable.
const toMs = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return NaN;
    return value < 1e12 ? value * 1000 : value; // <1e12 ⇒ seconds → ms
  }
  const t = Date.parse(value);
  return Number.isNaN(t) ? NaN : t;
};

// The moment a transaction was entered — prefer createdAt, then updatedAt, then
// fall back to the trade date (best-effort for legacy rows without metadata).
const getRecordedMs = (tx) => {
  const created = toMs(tx.createdAt);
  if (Number.isFinite(created)) return created;
  const updated = toMs(tx.updatedAt);
  if (Number.isFinite(updated)) return updated;
  return toMs(tx.date);
};

/**
 * Build a lookup of splits keyed by UPPERCASE symbol from the technicals map.
 * Only symbols that actually have splits are included.
 * @param {Object} technicals - map { [symbol]: { splits: [{date, factor}], ... } }
 * @returns {Object} { [SYMBOL]: [{ date: 'YYYY-MM-DD', factor: number }] }
 */
export const buildSplitsBySymbol = (technicals = {}) => {
  const out = {};
  if (!technicals || typeof technicals !== 'object') return out;
  for (const [sym, data] of Object.entries(technicals)) {
    const splits = data && data.splits;
    if (Array.isArray(splits) && splits.length > 0) {
      const clean = splits.filter(s => s && s.date && Number.isFinite(s.factor) && s.factor > 0 && s.factor !== 1);
      if (clean.length > 0) out[String(sym).toUpperCase()] = clean;
    }
  }
  return out;
};

/**
 * Return a NEW transactions array with BUY/SELL quantity & price adjusted for
 * any stock splits that occurred after each transaction was recorded. Raw input
 * is never mutated. Adjusted rows carry a `_splitFactor` marker (>1 or <1) for
 * optional UI hints. Dividends and other types pass through unchanged.
 *
 * @param {Array} transactions
 * @param {Object} splitsBySymbol - from buildSplitsBySymbol()
 * @returns {Array}
 */
export const applySplitAdjustments = (transactions = [], splitsBySymbol = {}) => {
  if (!Array.isArray(transactions) || transactions.length === 0) return transactions;
  if (!splitsBySymbol || Object.keys(splitsBySymbol).length === 0) return transactions;

  return transactions.map((tx) => {
    if (!tx || (tx.type !== 'BUY' && tx.type !== 'SELL')) return tx;
    const splits = splitsBySymbol[String(tx.symbol || '').toUpperCase()];
    if (!splits || splits.length === 0) return tx;

    const recordedMs = getRecordedMs(tx);
    // Cumulative factor of every split whose ex-date is AFTER this record was entered.
    let cumFactor = 1;
    for (const s of splits) {
      const splitMs = toMs(s.date);
      if (Number.isFinite(splitMs) && Number.isFinite(recordedMs) && splitMs > recordedMs) {
        cumFactor *= s.factor;
      }
    }
    if (cumFactor === 1) return tx;

    const qty = Number(tx.quantity) || 0;
    const price = Number(tx.price) || 0;
    return {
      ...tx,
      quantity: qty * cumFactor,
      price: cumFactor !== 0 ? price / cumFactor : price,
      // feeTax intentionally unchanged (total fee is split-invariant).
      _splitFactor: cumFactor,
    };
  });
};
